import { useApplicationModals } from "app/common/hooks";
import { ModalMolecule } from "app/components/molecules/modal";
import api from "app/integration/api";
import { useEffect, useRef, useState } from "react";
import { CameraIcon } from "@heroicons/react/24/outline";
import { Html5QrcodeScanner } from "html5-qrcode";
import "app/styles/qrscanner.css";

function ScanProductQrCode({ onSuccess = () => {} }) {
  const { showStates, closeModal, getPayloads, setPayloads } = useApplicationModals();
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const qrScannerRef = useRef(null);
  const [productVariants, setProductVariants] = useState([]);
  
  // Log modal state for debugging
  useEffect(() => {
    console.log("QR Scanner modal state:", { showStates, modalOpen: showStates["product.scanQrCode"] });
  }, [showStates]);
  
  // Fetch product variants when the modal opens
  useEffect(() => {
    if (showStates["product.scanQrCode"]) {
      fetchProductVariants();
    }
  }, [showStates]);
  
  // Fetch product variants from the API
  const fetchProductVariants = async () => {
    try {
      setLoading(true);
      const response = await api.productVariant.index();
      if (response.data) {
        setProductVariants(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching product variants:", error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal("product.scanQrCode");
  };

  const handleSuccess = async (qrCode) => {
    console.log("QR code scanned:", qrCode.trim());
    
    try {
      setLoading(true);
      
      // Find the product variant with this QR code
      const productVariant = productVariants?.find(
        (item) => item.qr_code === qrCode.replace(/"/g, "")
      );
      
      console.log("Found product variant:", productVariant);
      
      if (!productVariant) {
        console.warn(`Product variant not found for QR code: ${qrCode}`);
        setPayloads("errorMessage.view", {
          title: "Product Not Found",
          message: `No product found with QR code: ${qrCode}`
        });
        setLoading(false);
        return;
      }
      
      // Get product details
      const productResponse = await api.product.show(productVariant.product_id);
      const product = productResponse.data;
      
      // Get size and color details
      const sizeResponse = await api.sizeVariant.show(productVariant.size_variant_id);
      const colorResponse = await api.colorVariant.show(productVariant.color_variant_id);
      
      // Format the data to match what handleSuccessProductChoose expects
      const formattedProduct = {
        product: {
          id: productVariant.product_id,
          name: product?.name,
          code: product?.code,
          price: productVariant?.product_detail?.price || product?.price || 0,
          weight: productVariant?.product_detail?.weight || product?.weight || 0
        },
        variant: {
          size: {
            id: productVariant.size_variant_id,
            name: sizeResponse?.data?.name || 'Unknown Size'
          },
          color: {
            id: productVariant.color_variant_id,
            name: colorResponse?.data?.name || 'Unknown Color'
          }
        }
      };
      
      console.log("Formatted product for handleSuccessProductChoose:", formattedProduct);
      
      // Pass the formatted product to the onSuccess callback
      onSuccess(formattedProduct);
      
      // Show a success message
      setProduct({
        name: product?.name,
        code: product?.code,
        size: sizeResponse?.data?.name || 'Unknown Size',
        color: colorResponse?.data?.name || 'Unknown Color'
      });
      
      // Don't close the modal - keep scanning
    } catch (error) {
      console.error("Error processing QR code:", error);
      setPayloads("errorMessage.view", {
        title: "Error",
        message: "An error occurred while processing the QR code. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  // We don't need the searchProductByQrCode function anymore
  // as we're directly passing the QR code to the parent component

  // QR Scanner effect
  useEffect(() => {
    // Only initialize when modal is shown
    if (showStates["product.scanQrCode"]) {
      let initAttempts = 0;
      const maxAttempts = 5;
      
      // Clean up any existing scanner
      if (qrScannerRef.current) {
        qrScannerRef.current.clear();
        qrScannerRef.current = null;
      }
      
      // Function to initialize the scanner
      const initializeScanner = () => {
        // Check if the element exists before initializing
        const scannerElement = document.getElementById("product-qr-code-scanner");
        if (!scannerElement) {
          initAttempts++;
          if (initAttempts < maxAttempts) {
            console.log(`Scanner element not found in DOM. Attempt ${initAttempts}/${maxAttempts}. Retrying in 300ms...`);
            return setTimeout(initializeScanner, 300);
          } else {
            console.error("Failed to find scanner element after multiple attempts");
            return;
          }
        }
        
        console.log("Initializing QR scanner with element:", scannerElement);
        
        // Create new scanner with improved configuration
        const qrScanner = new Html5QrcodeScanner(
          "product-qr-code-scanner",
          { 
            fps: 10, 
            qrbox: { width: 250, height: 250 },
            formatsToSupport: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], // Support all formats
            disableFlip: false,
            rememberLastUsedCamera: true,
            aspectRatio: 1.0,
            showTorchButtonIfSupported: true
          },
          /* verbose= */ false
        );

        qrScanner.render(
          // Success callback
          (decodedText) => {
            try {
              // Clean up the scanned text - remove %22 (URL-encoded double quotes) and actual quotes
              const cleanedText = decodedText.replace(/%22/g, "").replace(/"/g, "");
              console.log(`QR Code detected: ${cleanedText}`);
              
              // Process the scanned QR code
              handleSuccess(cleanedText);
              
              // Pause scanning after successful detection
              if (qrScannerRef.current) {
                qrScannerRef.current.pause();
                
                // Resume scanning after a delay
                setTimeout(() => {
                  if (qrScannerRef.current) {
                    qrScannerRef.current.resume();
                  }
                }, 1000);
              }
            } catch (error) {
              console.error("Error processing QR code:", error);
              // Continue scanning even if there's an error
            }
          }, 
          // Error callback
          (errorMessage) => {
            // These are usually just failed scans, not actual errors
            // Only log if it's not the common "NotFoundException" which happens when no QR is detected
            if (!errorMessage.includes("NotFoundException")) {
              console.error(`QR Code scanning error: ${errorMessage}`);
            }
            // Don't show errors to the user for failed scans as this is normal operation
          }
        );

        qrScannerRef.current = qrScanner;
      }
      
      // Start the initialization process
      const initialTimeout = setTimeout(initializeScanner, 300);
      
      // Clean up on unmount
      return () => {
        clearTimeout(initialTimeout);
        if (qrScannerRef.current) {
          qrScannerRef.current.clear();
          qrScannerRef.current = null;
        }
      };
    }
  }, [showStates, productVariants]);

  return (
    <ModalMolecule
      show={showStates["product.scanQrCode"]}
      title="Scan Product QR Code"
      onClose={handleClose}
      size="md"
    >
      <div className="space-y-6">
        {/* The QR scanner container - this must be rendered before the scanner is initialized */}
        <div 
          id="product-qr-code-scanner" 
          className="qr-scanner-container" 
          key="qr-scanner-container"
          style={{ minHeight: '300px', width: '100%' }}
        ></div>
        <div className="text-center text-sm text-gray-500 mt-2">
          Point your camera at a product QR code to scan
        </div>

        {loading && (
          <div className="text-center py-4">
            <span className="text-gray-500">Initializing scanner...</span>
          </div>
        )}

        {!loading && product && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Product added!</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>{product.name} ({product.code})</p>
                  <p className="text-xs mt-1">{product.color} / {product.size}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div className="text-center py-4">
            <span className="text-gray-500">Scan a product QR code to add it to your order</span>
            <p className="text-xs text-gray-400 mt-1">Make sure the QR code is well-lit and centered in the camera view</p>
            <p className="text-xs text-gray-400">You can scan multiple products without closing this window</p>
          </div>
        )}
      </div>
    </ModalMolecule>
  );
}

export default ScanProductQrCode;
